/**
 * Composable for extracting dominant colors from images
 * Uses server-side API endpoint to bypass CORS restrictions
 * Falls back to client-side Canvas API for same-origin images
 */

// Cache to store extracted colors by image URL
const colorCache = new Map();

// Maximum cache size to prevent memory leaks
const MAX_CACHE_SIZE = 100;

/**
 * Extract dominant color from an image URL
 * @param {string} imageUrl - URL of the image to analyze
 * @returns {Promise<string>} - RGB color string in format "r, g, b"
 */
export const useImageColorExtraction = () => {
  /**
   * Load image and handle CORS
   * Tries multiple strategies: without crossOrigin first, then with crossOrigin
   * @param {string} url - Image URL
   * @param {boolean} useCrossOrigin - Whether to use crossOrigin attribute
   * @returns {Promise<HTMLImageElement>}
   */
  const loadImage = (url, useCrossOrigin = false) => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      
      // Set crossOrigin only if requested (for CORS-enabled servers)
      if (useCrossOrigin) {
        img.crossOrigin = 'anonymous';
      }
      
      img.onload = () => resolve(img);
      img.onerror = () => reject(new Error('Failed to load image'));
      
      img.src = url;
    });
  };

  /**
   * Extract pixel data from image using Canvas
   * @param {HTMLImageElement} img - Image element
   * @returns {ImageData} - Pixel data
   * @throws {Error} - If CORS blocks access to pixel data
   */
  const getImageData = (img) => {
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    
    // Set canvas size to image size (limit to reasonable size for performance)
    const maxSize = 200; // Analyze at max 200x200 for performance
    const scale = Math.min(maxSize / img.width, maxSize / img.height, 1);
    
    canvas.width = img.width * scale;
    canvas.height = img.height * scale;
    
    try {
      // Draw image to canvas
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      
      // Get pixel data - this will throw if CORS blocks it
      return ctx.getImageData(0, 0, canvas.width, canvas.height);
    } catch (error) {
      // CORS error when trying to read pixel data
      throw new Error('CORS blocked: Cannot read image data');
    }
  };

  /**
   * Find dominant color from pixel data
   * Filters out white, black, and gray colors to get vibrant colors
   * @param {ImageData} imageData - Pixel data from canvas
   * @returns {string} - RGB color string "r, g, b"
   */
  const findDominantColor = (imageData) => {
    const data = imageData.data;
    const colorCounts = new Map();
    const pixelCount = data.length / 4; // Each pixel is 4 values (RGBA)
    
    // Sample pixels (every 10th pixel for performance)
    const sampleRate = 10;
    
    for (let i = 0; i < data.length; i += 4 * sampleRate) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      
      // Skip transparent pixels
      if (a < 128) continue;
      
      // Calculate saturation to filter out grays
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const saturation = max === 0 ? 0 : (max - min) / max;
      
      // Skip very dark (black), very light (white), or low saturation (gray) colors
      const brightness = (r + g + b) / 3;
      if (brightness < 30 || brightness > 225 || saturation < 0.1) {
        continue;
      }
      
      // Quantize colors to reduce color space (group similar colors)
      const quantizedR = Math.floor(r / 10) * 10;
      const quantizedG = Math.floor(g / 10) * 10;
      const quantizedB = Math.floor(b / 10) * 10;
      
      const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
      const currentCount = colorCounts.get(colorKey) || 0;
      colorCounts.set(colorKey, currentCount + 1);
    }
    
    // If no vibrant colors found, fall back to most common color
    if (colorCounts.size === 0) {
      // Re-sample without filtering
      for (let i = 0; i < data.length; i += 4 * sampleRate) {
        const r = data[i];
        const g = data[i + 1];
        const b = data[i + 2];
        const a = data[i + 3];
        
        if (a < 128) continue;
        
        const quantizedR = Math.floor(r / 10) * 10;
        const quantizedG = Math.floor(g / 10) * 10;
        const quantizedB = Math.floor(b / 10) * 10;
        
        const colorKey = `${quantizedR},${quantizedG},${quantizedB}`;
        const currentCount = colorCounts.get(colorKey) || 0;
        colorCounts.set(colorKey, currentCount + 1);
      }
    }
    
    // Find the most common color
    let maxCount = 0;
    let dominantColor = '128, 128, 128'; // Default gray fallback
    
    colorCounts.forEach((count, colorKey) => {
      if (count > maxCount) {
        maxCount = count;
        dominantColor = colorKey;
      }
    });
    
    return dominantColor;
  };

  /**
   * Extract color using server-side API (bypasses CORS)
   * @param {string} imageUrl - URL of the image
   * @returns {Promise<string>} - RGB color string "r, g, b"
   */
  const extractColorViaServer = async (imageUrl) => {
    try {
      const response = await $fetch('/api/extract-color', {
        method: 'POST',
        body: { imageUrl }
      });

      if (response?.success && response?.color) {
        return response.color;
      }

      // Fallback to default if server extraction failed
      return response?.color || '128, 128, 128';
    } catch (error) {
      console.warn('Server-side color extraction failed:', error);
      return '128, 128, 128';
    }
  };

  /**
   * Main function to extract dominant color from image URL
   * Uses server-side API first, falls back to client-side for same-origin images
   * @param {string} imageUrl - URL of the image
   * @returns {Promise<string>} - RGB color string "r, g, b"
   */
  const extractDominantColor = async (imageUrl) => {
    // Return default if no URL provided
    if (!imageUrl) {
      return '128, 128, 128';
    }

    // Check cache first
    if (colorCache.has(imageUrl)) {
      const cachedColor = colorCache.get(imageUrl);
      // If cached value is null, it means extraction failed previously
      if (cachedColor === null) {
        return '128, 128, 128';
      }
      return cachedColor;
    }

    // Strategy 1: Use server-side API (works for all images, bypasses CORS)
    try {
      const color = await extractColorViaServer(imageUrl);
      
      // Cache the result (with size limit)
      if (colorCache.size >= MAX_CACHE_SIZE) {
        const firstKey = colorCache.keys().next().value;
        colorCache.delete(firstKey);
      }
      colorCache.set(imageUrl, color);
      
      return color;
    } catch (serverError) {
      // If server-side fails, try client-side for same-origin images
      if (!process.client || typeof document === 'undefined') {
        return '128, 128, 128';
      }

      try {
        let img;
        let imageData;
        
        // Strategy 2: Try client-side (works for same-origin images)
        try {
          img = await loadImage(imageUrl, false);
          imageData = getImageData(img);
        } catch (firstError) {
          // Try with crossOrigin
          try {
            img = await loadImage(imageUrl, true);
            imageData = getImageData(img);
          } catch (secondError) {
            throw new Error('Both server and client extraction failed');
          }
        }
        
        // Find dominant color
        const color = findDominantColor(imageData);
        
        // Cache the result
        if (colorCache.size >= MAX_CACHE_SIZE) {
          const firstKey = colorCache.keys().next().value;
          colorCache.delete(firstKey);
        }
        colorCache.set(imageUrl, color);
        
        return color;
      } catch (clientError) {
        // Both strategies failed
        console.warn('Color extraction failed (server and client):', clientError);
        
        // Cache the failure
        if (colorCache.size >= MAX_CACHE_SIZE) {
          const firstKey = colorCache.keys().next().value;
          colorCache.delete(firstKey);
        }
        colorCache.set(imageUrl, null);
        
        return '128, 128, 128';
      }
    }
  };

  /**
   * Clear the color cache (useful for memory management)
   */
  const clearCache = () => {
    colorCache.clear();
  };

  return {
    extractDominantColor,
    clearCache
  };
};

